/**
 * Display-only currency conversion (#43, 2026-06-12).
 *
 * ── WHAT THIS IS, AND IS NOT ──────────────────────────────────────────────
 * Money in Elreign is stored and handled in ZAR CENTS — that seam is
 * authoritative and UNCHANGED. This module adds a DISPLAY-ONLY option to
 * render an *approximate* USD figure ALONGSIDE the authoritative Rand, for
 * convenience when a non-SA reader is looking at a screen.
 *
 * The USD figure is computed from a single, hard-coded INDICATIVE rate
 * constant — NOT a live FX feed. It must never be mistaken for an
 * authoritative value: every USD render is accompanied by the "≈ USD,
 * indicative" label (see `USD_INDICATIVE_LABEL`) and the components that show
 * it carry a tooltip explaining the rate is illustrative.
 *
 * The canonical Rand formatter (`formatZarCents` in `lib/fmt-r.ts`) is the
 * single source of truth for ZAR rendering and is reused verbatim here — this
 * module never re-implements Rand formatting and never touches the cents↔rand
 * seam.
 */

import { formatZarCents } from '../fmt-r';

export type DisplayCurrency = 'ZAR' | 'USD';

export const DEFAULT_DISPLAY_CURRENCY: DisplayCurrency = 'ZAR';

/**
 * INDICATIVE ZAR→USD rate. A single constant, deliberately NOT a live feed.
 * Read as: 1 ZAR ≈ this many USD. Update manually if it drifts far from
 * reality; nothing in the product relies on it being current — it only ever
 * decorates an authoritative Rand figure.
 *
 * (≈ R18.50 to the dollar, i.e. 1 ZAR ≈ 0.054 USD, mid-2026 ballpark.)
 */
export const ZAR_TO_USD_INDICATIVE = 0.054;

/** The label that MUST accompany any USD figure so it can't be read as authoritative. */
export const USD_INDICATIVE_LABEL = '≈ USD, indicative';

/** Human-readable note for tooltips explaining the indicative nature. */
export const USD_INDICATIVE_TOOLTIP =
  'Approximate US dollar value at an indicative rate of ' +
  `1 ZAR ≈ ${ZAR_TO_USD_INDICATIVE} USD. Not a live exchange rate. ` +
  'All amounts are held and reported in South African Rand.';

/**
 * Format ZAR cents as an approximate USD string, e.g. "$ 8,320.50".
 *
 * Uses a regular `Intl.NumberFormat('en-US')` — USD is a foreign-convenience
 * display, so US grouping (comma thousands, period decimal) is correct and
 * intentional here (the SA-specific separators apply only to the Rand path).
 * The `≈` prefix is built in so a USD figure always reads as approximate.
 */
export function formatUsdFromZarCents(zarCents: number): string {
  const usd = (zarCents / 100) * ZAR_TO_USD_INDICATIVE;
  const body = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Math.abs(usd));
  const sign = usd < 0 ? '-' : '';
  // Plain ASCII spaces only. US convention puts NO space between the $ and the
  // amount; the leading "≈ " marks it approximate. Built with concatenation so
  // no stray non-break space can creep into a template literal.
  return '≈ ' + sign + '$' + body;
}

/**
 * Format ZAR cents for DISPLAY in the chosen currency.
 *
 *   - 'ZAR' (default + authoritative) → delegates to the canonical
 *     `formatZarCents` ("R 8 320,50").
 *   - 'USD' → the indicative approximation ("≈ $ 8,320.50"). Callers SHOULD
 *     pair this with `USD_INDICATIVE_LABEL` so the figure can't be mistaken
 *     for an authoritative one.
 *
 * `display` is the only behavioural difference; the cents input and the ZAR
 * math are untouched.
 */
export function formatDisplayAmount(
  zarCents: number,
  display: DisplayCurrency = DEFAULT_DISPLAY_CURRENCY,
): string {
  return display === 'USD' ? formatUsdFromZarCents(zarCents) : formatZarCents(zarCents);
}
